import { Button } from 'primereact/button';
import { Dispatch, FC, SetStateAction } from 'react';
import { DiagnosticReportDownloadCSV, DownloadData } from './DiagnosticReportDownloadCSV';
import { DiagnosticReportResetButton } from './DiagnosticReportResetButton';
import { ProjectionGroup } from '../../models/ProjectionGroupEnum';
import { InputText } from 'primereact/inputtext';
import { SortOrder, TableDataElement } from './DiagnosticReportStudentsTable';
import { MaskedInput } from '../MaskedData/MaskedInput';

interface Props {
  filters: { name: { value: string | null; matchMode: 'contains' } };
  setFilters: (a: { name: { value: string | null; matchMode: 'contains' } }) => void;
  tableData: TableDataElement[];
  downloadData: DownloadData[];
  setRefetchKey: Dispatch<SetStateAction<number>>;
  sortValue: SortOrder;
  setSelectedStudents: Dispatch<SetStateAction<TableDataElement[]>>;
  activeSelection: boolean;
  selectedStudents: TableDataElement[];
  setActiveSelection: Dispatch<SetStateAction<boolean>>;
  filterStatus: ProjectionGroup;
  setFilterStatus: Dispatch<SetStateAction<ProjectionGroup>>;
  filterCountSkills: 2 | 3;
}

const DiagnosticReportStudentTableHeader: FC<Props> = ({
  filters,
  setFilters,
  tableData,
  downloadData,
  setRefetchKey,
  sortValue,
  setSelectedStudents,
  selectedStudents,
  activeSelection,
  setActiveSelection,
  filterStatus,
  setFilterStatus,
  filterCountSkills,
}) => {
  // The status counts must reflect the same skill-per-week column the table filters on
  // (`twoSkills`/`threeSkills`), not the fixed `projectedStatus`, so they update with the toggle.
  const skillColumn: 'twoSkills' | 'threeSkills' = filterCountSkills === 2 ? 'twoSkills' : 'threeSkills';
  const countByStatus = (status: ProjectionGroup) => tableData.filter(el => el[skillColumn] === status).length;
  return (
    <div className="tw-flex tw-flex-col tw-gap-y-6 tw-w-full">
      <div className="tw-flex tw-flex-col tw-gap-y-4 lg:tw-flex-row lg:tw-gap-x-6 tw-w-full">
        <MaskedInput>
          <InputText
            value={filters.name.value || ''}
            onChange={e => setFilters({ ...filters, name: { ...filters.name, value: e.target.value || null } })}
            placeholder={'Search by student name'}
            className="lg:tw-w-2/3 !tw-font-atkinson !tw-border-neutral-100 !tw-rounded-full tw-border-4"
          />
        </MaskedInput>
        <div className="lg:tw-w-1/3 tw-flex tw-flex-row tw-gap-x-4 ">
          <DiagnosticReportResetButton
            allStudentsClass={tableData}
            setRefetchKey={setRefetchKey}
            selectedStudents={selectedStudents}
            setSelectedStudents={setSelectedStudents}
            activeSelection={activeSelection}
            setActiveSelection={setActiveSelection}
          />
          <DiagnosticReportDownloadCSV
            studentList={downloadData}
            filters={{
              searchTerm: filters.name.value,
              sortField: sortValue.field,
              sortOrder: sortValue.order,
            }}
          />
        </div>
      </div>
      <div className="tw-flex tw-flex-row tw-items-center tw-gap-x-3 tw-w-full">
        <span className="tw-font-atkinson tw-uppercase tw-font-bold tw-text-sm">Filter by status</span>
        <Button
          label={`All (${tableData.length})`}
          className={`!tw-py-1 !tw-px-3 ${
            filterStatus !== ProjectionGroup.ALL
              ? '!tw-border-indigo-400 !tw-border-2 !tw-text-indigo-500 hover:!tw-text-white hover:tw-bg-blue-800'
              : ''
          }`}
          outlined={filterStatus !== ProjectionGroup.ALL}
          onClick={() => setFilterStatus(ProjectionGroup.ALL)}
        />
        <Button
          label={`Approaching (${countByStatus(ProjectionGroup.AT_RISK)})`}
          className={`!tw-py-1 !tw-px-3 ${
            filterStatus !== ProjectionGroup.APPROACHING
              ? '!tw-border-indigo-400 !tw-border-2 !tw-text-indigo-500 hover:!tw-text-white hover:tw-bg-blue-800'
              : ''
          }`}
          outlined={filterStatus !== ProjectionGroup.APPROACHING}
          onClick={() => setFilterStatus(ProjectionGroup.APPROACHING)}
        />
        <Button
          label={`On track (${countByStatus(ProjectionGroup.ON_TRACK)})`}
          className={`!tw-py-1 !tw-px-3 ${
            filterStatus !== ProjectionGroup.ON_TRACK
              ? '!tw-border-indigo-400 !tw-border-2 !tw-text-indigo-500 hover:!tw-text-white hover:tw-bg-blue-800'
              : ''
          }`}
          outlined={filterStatus !== ProjectionGroup.ON_TRACK}
          onClick={() => setFilterStatus(ProjectionGroup.ON_TRACK)}
        />
      </div>
    </div>
  );
};

export default DiagnosticReportStudentTableHeader;
